import * as React from 'react';
import { forEach, isEqual, isNil } from 'lodash';
import { withTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';

import { COLOR_PALETTES, PaletteName } from 'styles/theme';
import validators from 'helpers/validators';
import { capitalize } from 'helpers/helpers';
import { camelize } from 'helpers/syntaxHelper';
import Selector from 'components/inputs/Selector';
import ColorSelector from 'components/inputs/ColorSelector';
import TextInfo from 'ui/TextInfo/TextInfo';

import './_form.scss';
import { CustomizableChartSettings, CustomPalette } from 'business/definitions';
import ColorMapper from 'components/inputs/ColorMapper/ColorMapper';
import { LegendDimension } from 'routes/Explore/components/Contexts/LegendDimensionContext/LegendDimensionContext';
import { trackEvent } from 'helpers/trackerHelpers';
import i18n, { translateComponent } from 'i18n';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import Flex from 'ui/Flex/Flex';
import SortingSelect from 'components/inputs/SortingSelect';
import { Box } from '@mui/system';

export type FormType =
    | 'text'
    | 'longText'
    | 'password'
    | 'email'
    | 'checkbox'
    | 'switch'
    | 'select'
    | 'colorSelect'
    | 'toggle'
    | 'sortingSelect'
    | 'staticText'
    | 'colorMapper'
    | 'number';

type FormValue =
    | string
    | boolean
    | number
    | CustomizableChartSettings[keyof CustomizableChartSettings]
    | null
    | undefined;

type TempController =
    | ((e: Event, value: FormValue) => FormValue)
    | ((value: FormValue) => FormValue)
    | ((event: React.ChangeEvent) => FormValue);

export type FormField = {
    disabled?: boolean;
    helperText?: string;
    adornment?: React.ReactNode;
    type: FormType;
    key: string;
    label: string | null;
    name: string;
    value?: FormValue;
    validators?: string[];
    errorMessages?: string[];
    options?: { value: FormValue; label: string }[];
    mappedValues?: LegendDimension;
    default?: FormValue;
    preventSorting?: boolean; // prevent sorting of options
    maxValue?: number;
    minValue?: number;
    defaultValue?: number;
};

export type FormProps = {
    showSubmit: boolean;
    isLoading: boolean;
    t: TranslationT;
    submit: FlowAnyFunction;
    onUpdate: FlowAnyFunction;
    className: string;
    confirmButtonColor: string;
    submitText: string;
    submitClass: string;
    fields: FormField[];
    errors: { [key: string]: string };
    leftButton: React.ReactNode;
};

type State = {
    [key: string]: FormValue;
};

// TODO: tear down this and use a 3rd party lib, such as https://react-hook-form.com/
class Form extends React.Component<FormProps, State> {
    static defaultProps = {
        submitClass: '',
        isLoading: false,
        submit: () => {},
        submitText: 'Confirmer',
        showSubmit: true,
        fields: [],
        errors: {},
        leftButton: null,
        className: '',
        confirmButtonColor: null,
        onUpdate: null,
    };

    constructor(props: FormProps) {
        super(props);

        const futureState: State = {};
        props.fields.forEach((field) => {
            this.createStateControlMethod(field);
            futureState[field.key] = field.value;
        });
        this.state = futureState;
    }

    shouldComponentUpdate(nextProps: FormProps, nextState: State) {
        const haveFieldsChanged = !isEqual(this.props.fields, nextProps.fields);
        let haveValuesChanged = false;
        nextProps.fields.forEach((field) => {
            if (nextState[field.key] !== this.state[field.key]) {
                haveValuesChanged = true;
            }
        });

        let hasErrors = Object.keys(nextProps.errors).length > 0;
        forEach(nextState, (value, key) => {
            if (key.endsWith('ErrorText') && value) hasErrors = true;
        });

        const hasLoadingChanged = this.props.isLoading !== nextProps.isLoading;
        return (
            haveFieldsChanged ||
            haveValuesChanged ||
            hasErrors ||
            hasLoadingChanged
        );
    }

    componentDidUpdate(prevProps: FormProps) {
        // Need to update the state if new props are received
        if (this.props !== prevProps) {
            const futureState = {};
            this.props.fields.forEach((field) => {
                this.createStateControlMethod(field);
                // @ts-expect-error [TS migration] Was not detected by flow
                futureState[field.key] = field.value;
            });
            // I have no choice but to call setState in componentDidUpdate
            // indeed componentWillReceiveProps is deprecated
            // https://fr.reactjs.org/docs/react-component.html#componentdidupdate
            this.setState(futureState);
        }
    }

    /**
     * Generates all the React components based on the fields passed as props.
     * Form validation is handled by react-material-ui-form-validator
     * @return {[type]} [description]
     */
    getFields = () =>
        this.props.fields.map((field, index) => {
            // @ts-expect-error [TS migration] (previously $FlowFixMe)
            let handleChangeFunction = this[`handle${field.name}Change`];
            if (!handleChangeFunction) {
                this.createStateControlMethod(field);
                // @ts-expect-error [TS migration] (previously $FlowFixMe)
                handleChangeFunction = this[`handle${field.name}Change`];
            }

            // If the state hasn't been set yet we take the value from the props
            const fieldValue =
                field.key in this.state ? this.state[field.key] : field.value;

            if (
                ['number', 'text', 'longText', 'password', 'email'].includes(
                    field.type,
                )
            ) {
                const errorText =
                    this.state[`${field.key}ErrorText`] ||
                    this.props.errors[field.key];

                const helperText =
                    field.helperText && !errorText
                        ? field.helperText
                        : errorText;

                let type = 'text';
                if (field.type === 'password') {
                    type = 'password';
                } else if (field.type === 'email') {
                    type = 'email';
                } else if (field.type === 'number') {
                    type = 'number';
                }
                let inputProps = null;
                if (field.adornment) {
                    inputProps = {
                        endAdornment: (
                            <InputAdornment position="end">
                                {field.adornment}
                            </InputAdornment>
                        ),
                    };
                }

                return (
                    <TextField
                        // behaviour
                        value={fieldValue ?? ''}
                        onChange={handleChangeFunction}
                        autoFocus={index === 0}
                        // style
                        className="form-control"
                        variant="outlined"
                        margin="dense"
                        size="small"
                        multiline={field.type === 'longText'}
                        rows={field.type === 'longText' ? 4 : 1}
                        // TODO StrictNullCheck : Try to remove "!"
                        InputProps={inputProps!}
                        fullWidth
                        // data/state
                        key={field.key}
                        label={field.label}
                        name={field.name}
                        type={type}
                        error={!!errorText}
                        disabled={field.disabled}
                        helperText={helperText as string}
                        onFocus={() => trackEvent(`form/${field.key}/focus`)}
                    />
                );
            }
            if (field.type === 'staticText') {
                return (
                    <Box
                        paddingTop={1}
                        key={`${field.key}-${field.name}-${field.label}`}
                    >
                        <Typography variant="body1SemiBold" sx={{ mt: '12px' }}>
                            <>
                                {i18n.t(field.name)}
                                {field.label && (
                                    <TextInfo
                                        tooltip={i18n.t(field.label) as string}
                                    >
                                        &nbsp;
                                    </TextInfo>
                                )}
                            </>
                        </Typography>
                    </Box>
                );
            }
            if (field.type === 'checkbox') {
                // checked needs to be Boolean, and not `undefined`
                // otherwise the Checkbox will be in uncontrolled mode, and it will lead to weird bugs
                return (
                    <FormControlLabel
                        key={field.key}
                        control={
                            <Checkbox
                                checked={Boolean(fieldValue)}
                                onChange={handleChangeFunction}
                                inputProps={{
                                    name: field.name,
                                }}
                                disabled={field.disabled}
                            />
                        }
                        label={field.label}
                    />
                );
            }
            if (field.type === 'switch') {
                // checked needs to be Boolean, and not `undefined`
                // otherwise the Switch will be in uncontrolled mode, and it will lead to weird bugs
                return (
                    <Tooltip title={field.helperText} key={field.key}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(fieldValue)}
                                    onChange={handleChangeFunction}
                                    inputProps={{
                                        name: field.name,
                                    }}
                                    disabled={field.disabled}
                                />
                            }
                            key={field.key}
                            label={field.label}
                            classes={{ root: `e2e-switch-${field.name}` }}
                        />
                    </Tooltip>
                );
            }
            if (field.type === 'select') {
                // Used only for chart settings, hence the translation
                return (
                    <Selector
                        key={field.key}
                        selectorId={field.key}
                        label={field.label ?? ''}
                        value={fieldValue ?? ''}
                        onChange={handleChangeFunction}
                        options={field.options ?? []}
                        disabled={field.disabled}
                        preventSorting={Boolean(field.preventSorting)}
                        variant="outlined"
                    />
                );
            }
            if (field.type === 'colorSelect') {
                return (
                    <ColorSelector
                        key={field.key}
                        value={typeof fieldValue === 'string' ? fieldValue : ''}
                        onChange={handleChangeFunction}
                        options={COLOR_PALETTES.map(
                            (palette: {
                                name: string;
                                colors: Array<string>;
                            }) => ({
                                ...palette,
                                value: palette.name,
                                label: capitalize(
                                    this.props.t(
                                        `colorPalette.${palette.name}`,
                                    ),
                                ),
                            }),
                        )}
                    />
                );
            }
            if (field.type === 'colorMapper') {
                return (
                    <ColorMapper
                        key={field.key}
                        defaultPaletteName={field.default as PaletteName}
                        currentPalette={field.value as CustomPalette}
                        legendDimension={field.mappedValues}
                        onChange={handleChangeFunction}
                    />
                );
            }
            if (field.type === 'sortingSelect') {
                return (
                    <SortingSelect
                        field={field}
                        key={field.key}
                        onChange={handleChangeFunction}
                    />
                );
            }
            if (field.type === 'toggle' && field.options) {
                const { options } = field;
                return (
                    <Flex direction="column" key={field.key}>
                        <Typography variant="body1">{field.label}</Typography>
                        <ToggleButtonGroup
                            color="primary"
                            sx={{ width: '100%' }}
                            value={field.value ?? field.default}
                            exclusive
                            onChange={handleChangeFunction}
                        >
                            {options.map((option): React.ReactNode => {
                                if (isNil(option?.value)) {
                                    console.error(
                                        'There should be a value for option',
                                        option?.label,
                                    );
                                }
                                return (
                                    <ToggleButton
                                        size="small"
                                        value={option.value ?? ''}
                                        key={`toggle-${option.value}`}
                                        sx={{
                                            width: `${100 / options.length}%`,
                                        }}
                                    >
                                        {translateComponent(option.label)}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>
                    </Flex>
                );
            }
            return null;
        });

    /**
     * Add a method to each editable field to turn them into controlled components
     * @param  {Object} field editable field
     * @return {null}
     */
    createStateControlMethod = (field: FormField) => {
        let tempController: TempController;
        if (
            ['number', 'text', 'longText', 'password', 'email'].includes(
                field.type,
            )
        ) {
            tempController = (
                e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
                let { value }: { value: FormValue } = e.target;
                if (field.type === 'number') {
                    if (
                        value &&
                        !(value.endsWith('.') || value.endsWith(',')) &&
                        !(value.length === 1 && value === '-')
                    ) {
                        value = parseFloat(value.replace(',', '.'));
                    }
                }
                if (field.type === 'email') {
                    value = value.toString().trim();
                }
                this.setState({ [field.key]: value });
                return value;
            };
        } else if (field.type === 'checkbox') {
            tempController = (e: Event, value: FormValue) => {
                this.setState({ [field.key]: value });
                return value;
            };
        } else if (field.type === 'switch') {
            tempController = (e: Event, value: FormValue) => {
                this.setState({ [field.key]: value });
                return value;
            };
        } else if (field.type === 'toggle') {
            tempController = (e: Event, value: FormValue) => {
                if (value !== null) {
                    this.setState({ [field.key]: value });
                    return value;
                }
                return this.state[field.key];
            };
        } else if (
            ['select', 'colorSelect', 'colorMapper', 'sortingSelect'].includes(
                field.type,
            )
        ) {
            tempController = (value: FormValue) => {
                this.setState({ [field.key]: value });
                return value;
            };
        }
        const controller = this.props.onUpdate
            ? // @ts-expect-error [TS migration] Was not detected by flow
              (eventValue, value) => {
                  const stateValue = tempController(eventValue, value);
                  trackEvent(`form/${field.key}`);
                  // We need to manually update the state in case tempController doesn't have enough time
                  this.props.onUpdate({
                      ...this.state,
                      [field.key]: stateValue,
                  });
              }
            : // @ts-expect-error [TS migration] Was not detected by flow
              tempController;
        // @ts-expect-error [TS migration] Was not detected by flow
        this[`handle${field.name}Change`] = controller.bind(this);
    };

    /**
     * Function handling the validation of the form and triggering the submit function.
     * The event normally refresh the page, hence the preventDefault()
     * We go through every validator for each field. For the 'match' validator,
     * the second part of the string is used as the key of the field to compare against.
     * For example matchPassword will match against 'password' field, and matchEmail against
     * 'email' field/
     * @param  {Event} e Event fired by the button or the form submitted
     */
    // @ts-expect-error [TS migration] Was not detected by flow
    handleSubmit = (e) => {
        e.preventDefault();
        const formData = {};
        let isValid = true;
        const state: State = { ...this.state };
        const futureState = {};
        this.props.fields.forEach((field) => {
            const fieldValue = state[field.key];
            // Check that field is valid
            if (field.validators) {
                field.validators.forEach((validator) => {
                    let isFieldValid;
                    if (validator.match(/^match/)) {
                        // Special case for match password.
                        const matchKey = camelize(validator.slice(5));
                        isFieldValid = validators.match(
                            // @ts-expect-error [TS migration] (previously $FlowFixMe) the objects can be booleans and the function takes strings
                            state[matchKey],

                            fieldValue,
                        );
                    } else {
                        // @ts-expect-error [TS migration] Was not detected by flow
                        isFieldValid = validators[validator](fieldValue);
                    }

                    if (!isFieldValid) {
                        isValid = false;
                        // @ts-expect-error [TS migration] Was not detected by flow
                        futureState[`${field.key}ErrorText`] = this.props.t(
                            `validators.${validator}`,
                        );
                    } else {
                        // @ts-expect-error [TS migration] Was not detected by flow
                        futureState[`${field.key}ErrorText`] = null;
                    }
                });
            }
            // @ts-expect-error [TS migration] Was not detected by flow
            formData[field.key] = fieldValue;
        });
        this.setState({ ...futureState });
        if (isValid) {
            this.props.submit(formData);
        }
    };

    render() {
        const fields = this.getFields();
        return (
            <form className={this.props.className} onSubmit={this.handleSubmit}>
                {fields.length > 0 && (
                    <div className="form-body e2e-form-element">{fields}</div>
                )}
                {this.props.showSubmit && (
                    <DialogActions className="form-buttons">
                        {this.props.leftButton}
                        {!!this.props.isLoading && <CircularProgress />}
                        {!this.props.isLoading && (
                            <Button
                                data-testid={`modal-submit-button-${this.props.submitText}`}
                                variant="contained"
                                className={`${this.props.submitClass} e2e-modal-submit-button`}
                                style={{
                                    background: this.props.confirmButtonColor
                                        ? this.props.confirmButtonColor
                                        : undefined,
                                }}
                                type="submit"
                            >
                                {this.props.submitText}
                            </Button>
                        )}
                    </DialogActions>
                )}
            </form>
        );
    }
}

const TranslatedForm = withTranslation('components')(Form);
TranslatedForm.displayName = 'Form';
export default TranslatedForm;
